import ReactTable from '../Components/ReactTable';
import React, {Fragment, useMemo, useState} from 'react';
import _ from 'lodash';
import {getNicelyFormattedNumber} from "../../utils/formatUtil";
import Chart from "react-apexcharts";
import Select from "react-select";

export const GenericCostDisplay = ({header, fileContents, filePath, extraHeader}) => {
    const columnsByDate = _.groupBy(fileContents, 'header');
    const pivotedRows = _.map(columnsByDate, (columnsForDate) => {
        return columnsForDate.reduce((tableRow, cost) => {
            const costValue = typeof cost.cost === 'string' ? parseFloat(cost.cost.replace(/[$,]/g, '')) : (cost.cost ?? 0);
            return {
                ...tableRow,
                header: cost.header,
                cost_category: cost?.cost_category,
                [cost.date]: (tableRow[cost.date] ?? 0) + costValue
            }
        }, {})
    })
    const categoriesList = useMemo(() => _.map(pivotedRows, 'header').sort(), [pivotedRows]);
    const categoryColors = useMemo(() => {
        const colors = [
            '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0',
            '#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800',
            '#33b2df', '#546E7A', '#d4526e', '#13d8aa', '#A5978B',
            '#2b908f', '#f9a3a4', '#90ee7e', '#f48024', '#69d2e7'
        ];
        return categoriesList.reduce((acc, cat, idx) => {
            acc[cat] = colors[idx % colors.length];
            return acc;
        }, {});
    }, [categoriesList]);

    const [selectedCategories, setSelectedCategories] = useState([]);
    const [view, setView] = useState('Table');

    const headers = _.chain(fileContents)
        .map(x => x.date)
        .uniq()
        .sort()
        .reverse()
        .map(x => ({
            header: x,
            accessorKey: x,
            footer: (row) => {
                const displayedRowsSum = row.table.getFilteredRowModel().rows
                    .reduce((sum, item) => sum + (item.original[x] ?? 0), 0)
                return <span>${getNicelyFormattedNumber(displayedRowsSum, 2)}</span>;
            }
        }))
        .value();

    const categories = _.map(headers, 'header');
    const seriesData = !_.isEmpty(selectedCategories) ? _.filter(pivotedRows, row => selectedCategories.includes(row.header)) : pivotedRows;
    const series = _.chain(seriesData)
        .map(row => ({
            name: row.header,
            data: _.map(categories, date => row[date] ?? 0),
            color: categoryColors[row.header]
        }))
        .filter(s => _.some(s.data, val => val > 0))
        .value();

    const options = {
        chart: {id: `generic-cost-chart-${filePath}`, stacked: true, zoom: {enabled: false}},
        plotOptions: {bar: {horizontal: true}},
        dataLabels: {
            enabled: true,
            formatter: function (val, {dataPointIndex, w}) {
                if (val === 0) return '';
                const total = w.globals.stackedSeriesTotals[dataPointIndex];
                const percentage = (val / total) * 100;
                return isNaN(percentage) ? '0%' : percentage.toFixed(0) + '%';
            }
        },
        xaxis: {
            categories: categories,
            labels: {formatter: (value) => `$${getNicelyFormattedNumber(value, 0)}`}
        },
        yaxis: {labels: {formatter: (value) => value}},
        tooltip: {y: {formatter: (value) => `$${getNicelyFormattedNumber(value, 2)}`}}
    };

    const dot = (color = '#ccc') => ({
        alignItems: 'center',
        display: 'flex',

        ':before': {
            backgroundColor: color,
            borderRadius: 10,
            content: '" "',
            display: 'block',
            marginRight: 8,
            height: 10,
            width: 10,
        },
    });

    const colourStyles = {
        option: (styles, {data}) => ({...styles, ...dot(data.color)}),
        multiValue: (styles, {data}) => ({...styles, ...dot(data.color)}),
        multiValueLabel: (styles) => ({...styles, color: 'black'}),
    };

    return (
        <div className={'generic-cost-display-container'}>
            <h2 id={filePath}>{header}</h2>
            <div className={'toggle-radio-button-container'}>
                {['Table', 'Chart'].map(option => {
                    const id = _.kebabCase(`toggle-radio-button-${option}-${filePath}`);
                    return <Fragment key={id}>
                        <input className={'toggle-radio-button'}
                               data-testid={_.kebabCase(`toggle-radio-button-${option}`)}
                               type="radio"
                               id={id}
                               checked={option === view}
                               onChange={() => setView(option)}
                        />
                        <label
                            className={`toggle-radio-button-label ${view === option ? 'toggle-radio-button-selected' : ''}`}
                            htmlFor={id}>{option}</label>
                    </Fragment>
                })}
            </div>
            {view === 'Table' ? (
                <ReactTable data={pivotedRows}
                            filterable
                            columns={[
                                {
                                    header: 'ID',
                                    accessorKey: 'header',
                                    sticky: "left",
                                    size: 250,
                                    footer: () => <span>Totals</span>
                                },
                                ...extraHeader,
                                ...headers
                            ]}
                />
            ) : (
                <div className={'generic-cost-display-chart-contaier'}>
                    <div className="chart-filter-container">
                        <Select id={`${header}_dropdown`}
                                data-testid={`${header}-test-id`}
                                isMulti
                                options={categoriesList.map(c => ({label: c, value: c, color: categoryColors[c]}))}
                                onChange={(options) => setSelectedCategories(options ? options.map(o => o.value) : [])}
                                value={selectedCategories.map(c => ({label: c, value: c, color: categoryColors[c]}))}
                                isClearable={true}
                                placeholder="All Categories"
                                styles={colourStyles}
                        />
                    </div>
                    <Chart
                        options={options}
                        series={series}
                        type="bar"
                        height={600}
                    />
                </div>
            )}
        </div>
    );
}